import { useState, useEffect } from 'react';
import { StoreReviewsPage } from './store/StoreReviewsPage';
import { DesktopSellerStoreReviewsPage } from './desktop/DesktopSellerStoreReviewsPage';
import { sellerService } from '@/services/seller';

/**
 * 판매자용 반응형 리뷰 관리 페이지
 * 화면 크기에 따라 모바일/데스크톱 뷰를 자동 전환
 * 단일책임: 판매자의 리뷰 관리를 위한 반응형 뷰 제공
 */
export function ResponsiveSellerStoreReviewsPage() {
  const [isMobile, setIsMobile] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 판매자의 레스토랑 ID 가져오기 (모바일에서 사용)
  useEffect(() => {
    const loadRestaurantId = async () => {
      try {
        const result = await sellerService.getSellerRestaurantId();
        if (result.success && result.restaurantId) {
          setRestaurantId(result.restaurantId);
        } else {
          setError(result.error || '레스토랑 정보를 찾을 수 없습니다');
        }
      } catch (err) {
        console.error('Failed to load restaurant ID:', err);
        setError('레스토랑 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    // 모바일에서만 restaurantId 필요 (데스크톱은 자체적으로 처리)
    if (isMobile) {
      loadRestaurantId();
    } else {
      setIsLoading(false);
    }
  }, [isMobile]);

  // 모바일에서 로딩 중이거나 에러가 있는 경우
  if (isMobile && (isLoading || error || !restaurantId)) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">로딩 중...</p>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      );
    }
  }

  // 모바일: 읽기 전용 StoreReviewsPage 사용
  // 데스크톱: DesktopSellerStoreReviewsPage 사용 (자체적으로 restaurantId 처리)
  return isMobile ? (
    <StoreReviewsPage storeId={restaurantId} readonly={true} />
  ) : (
    <DesktopSellerStoreReviewsPage />
  );
}